import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Tabs, Tab, Box, TabScrollButton, useMediaQuery, useTheme } from "@mui/material";
import ProjectTab from "./ProjectTab";
import { useJoyride } from "utilities/JoyrideProvider";
import { trackEvent } from "utilities/analytics";

function CustomTabPanel(props) {
  const { children, value, index, tabname } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${tabname}`}
      aria-labelledby={`${tabname}`}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const customOrder = [
  "ecosystem structure",
  "ecosystem function",
  "species population",
  "community composition",
  "species traits",
  "project statistics",
];

// Folder-tab palette: light → dark across the six tabs.
// Last color matches the existing NatureAnalytics accent (#164A1A).
const tabPalette = {
  "ecosystem structure":   { bg: "#D8E8C4", text: "#2B2B2B" },
  "ecosystem function":    { bg: "#B7D49A", text: "#2B2B2B" },
  "species population":    { bg: "#8FBB6D", text: "#1F3310" },
  "community composition": { bg: "#6B9E50", text: "#FFFFFF" },
  "species traits":        { bg: "#3E7D33", text: "#FFFFFF" },
  "project statistics":    { bg: "#164A1A", text: "#FFFFFF" },
};

function sortByCustomOrder(arr) {
  const orderMap = new Map(customOrder.map((name, index) => [name, index]));
  return arr.sort((a, b) => (orderMap.get(a.name) - orderMap.get(b.name)));
}

const ProjectDetailNavTabs = React.memo(({ tabsData, isLoadingTabs, compactProjectDetail, projectName }) => {

  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const { joyrideState } = useJoyride();
  const [value, setValue] = useState(0);

  // A tab is "renderable" only if it has something the user can actually see.
  // A MetricsGroup with metrics:[] renders nothing, so metrics.length alone
  // over-reports and makes empty tabs look broken to the user.
  const sortedTabsData = sortByCustomOrder(tabsData).filter(tab => {
    const { charts, metrics, tables } = tab.content;
    if (charts.length) return true;
    if (tables && tables.length) return true;
    return metrics.some(m =>
      m.type !== "metrics_group" ||
      (Array.isArray(m.metrics) && m.metrics.length > 0)
    );
  });

  const handleChange = (event, newValue) => {
    setValue(newValue);
    const tab = sortedTabsData[newValue];
    if (tab) {
      trackEvent("select_content", {
        content_type: "project_tab",
        content_id: tab.name,
        item_name: projectName || "",
      });
    }
  };

  const tabClassNames = {
    "ecosystem structure": "Help-projectpage-ecosystemstructure",
    "ecosystem function": "Help-projectpage-ecosystemfunctioning",
    "species population": "Help-projectpage-speciespopulation",
    "community composition": "Help-projectpage-communitycomposition",
    "species traits": "Help-projectpage-speciestraits",
    "project statistics": "Help-projectpage-projectstatistics",
  };

  const handleJoyrideStepChange = useCallback((stepIndex) => {
    const tabSteps = [17, 18, 19, 20, 21, 22];
    if (tabSteps.includes(stepIndex)) {
      const nextTabIndex = stepIndex - 16;
      if (nextTabIndex >= 0 && nextTabIndex < sortedTabsData.length) {
        setValue(nextTabIndex);
      }
    }
  },[sortedTabsData.length]);

  useEffect(() => {
    handleJoyrideStepChange(joyrideState.stepIndex);
  }, [joyrideState.stepIndex, handleJoyrideStepChange]);


  return (
    <>
      <Box
        className="Help-projectpage-tabs"
        sx={{
          borderBottom: 1,
          borderTop: 1,
          borderColor: "#D8D5CC",
          position: "sticky",
          top: "96px",
          [theme.breakpoints.down("md")]: { top: "152px" },
          [theme.breakpoints.down("sm")]: { top: "133px" },
          background: "#F9F5EB",
          zIndex: 999,
          width: "100%",
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="project detail tabs"
          variant="scrollable"
          scrollButtons
          allowScrollButtonsMobile
          sx={{
            background: "#F9F5EB",
            minHeight: "54px",
            paddingTop: "8px",
            // Buffer for the active tab's glow on the rightmost visible tab —
            // without this, the shadow blur extends past the scroller's right
            // edge and triggers MUI's scrollable auto-scroll on click.
            paddingRight: "16px",
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTabs-flexContainer": { alignItems: "flex-end" },
          }}
          ScrollButtonComponent={(props) => (
            <TabScrollButton {...props} disabled={(value === 0 && props.direction === "left") || (value === sortedTabsData.length - 1 && props.direction === "right")} />
          )}
        >
          {sortedTabsData.map((tab, idx) => {
            const palette = tabPalette[tab.name] || { bg: "#6B9E50", text: "#FFFFFF" };
            const isActive = value === idx;
            return (
              <Tab
                key={idx}
                label={tab.name}
                id={tab.name.replace(' ','')}
                aria-controls={`tabpanel-${tab.name.replace(' ','')}`}
                disableRipple
                sx={{
                  // Folder-tab shape: rounded top, flat bottom that sits on the content panel.
                  // Uniform height across active/inactive — difference would cause a non-
                  // animated 6px jump on each activation.
                  minHeight: "46px",
                  marginLeft: idx === 0 ? 0 : "-10px",
                  paddingLeft: "22px",
                  paddingRight: "22px",
                  paddingTop: "12px",
                  paddingBottom: "8px",
                  borderTopLeftRadius: "14px",
                  borderTopRightRadius: "14px",
                  backgroundColor: palette.bg,
                  color: palette.text,
                  opacity: isActive ? 1 : 0.82,
                  // Stack: active tab sits above its neighbors; tabs further right sit below those to their left.
                  zIndex: isActive ? sortedTabsData.length + 1 : sortedTabsData.length - idx,
                  // Shadow is purely vertical — any horizontal bleed on the LAST tab extends
                  // past the Tabs container's right edge and triggers MUI's scrollable
                  // variant to auto-scroll, which was the visible "jump" on project statistics.
                  // Tight blur so the glow can't bleed past the container's right
                  // edge for whichever tab happens to be rightmost visible after
                  // empty tabs are filtered out.
                  boxShadow: isActive
                    ? "0 -3px 5px rgba(22, 74, 26, 0.22)"
                    : "0 -1px 2px rgba(0, 0, 0, 0.08)",
                  transform: isActive ? "translateY(0)" : "translateY(2px)",
                  transition: "transform 120ms ease, opacity 120ms ease, box-shadow 120ms ease",
                  textTransform: "uppercase",
                  fontSize: "12px",
                  fontWeight: isActive ? 600 : 500,
                  lineHeight: "17px",
                  letterSpacing: "0.04em",
                  width: isSmall || isMedium ? "calc(100vw - 80px)" : "auto",
                  "&.Mui-selected": { color: palette.text },
                  "&:hover": {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                }}
              />
            );
          })}
        </Tabs>
      </Box>
      <Box sx={{ width: "100%", background: "#F2EEE3" }}>
        {sortedTabsData.map((tab, idx) => (
          <CustomTabPanel 
            key={idx} 
            value={value} 
            index={idx} 
            className={tabClassNames[tab.name]}
            tabname={tab.name.replace(' ','')}>
            <ProjectTab tabData={tab} isLoadingTabs={isLoadingTabs} compactProjectDetail={compactProjectDetail} projectName={projectName}/>
          </CustomTabPanel>
        ))}
      </Box>
    </>
  );
});

export default ProjectDetailNavTabs;

